"use client";

import React, { useEffect, useMemo } from "react";
import {
  ConsentProvider,
  ConsentBanner,
  ConsentDialog,
  useConsent,
  type ConsentConfig,
} from "@studio3dx/react-consent";
import posthog from "posthog-js";
import harbor from "@studio3dx/harbor-js";
import Script from "next/script";

const GA_ID = process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID;
const HARBOR_SITE_TOKEN = process.env.NEXT_PUBLIC_HARBOR_SITE_TOKEN;

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}
function GtagConsentDefault() {
  useEffect(() => {
    if (typeof window === "undefined" || !window.gtag || !GA_ID) return;
    window.gtag("consent", "default", {
      analytics_storage: "denied",
      ad_storage: "denied",
    });
  }, []);
  return null;
}

function GtagScript() {
  if (!GA_ID) return null;
  return (
    <>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`}
        strategy="afterInteractive"
      />
      <Script id="gtag-init" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('consent', 'default', {
            analytics_storage: 'denied',
            ad_storage: 'denied'
          });
        `}
      </Script>
      <GtagConsentDefault />
    </>
  );
}

function ShowCookieModalListener() {
  const { openDialog } = useConsent();
  useEffect(() => {
    const handleShow = () => openDialog();
    window.addEventListener("showCookieModal", handleShow);
    return () => window.removeEventListener("showCookieModal", handleShow);
  }, [openDialog]);
  return null;
}

function ConsentUI() {
  return (
    <>
      <ShowCookieModalListener />
      <ConsentBanner
        message="This website uses cookies, pixel tags, and local storage for performance, personalization, and marketing purposes. We use our own cookies and some from third parties. Only essential cookies are turned on by default."
        acceptAllLabel="Allow all cookies"
        rejectAllLabel="Do not allow cookies"
        managePreferencesLabel="Cookies settings"
        position="bottom-right"
        classNames={{
          root: "!bg-black !text-white !shadow-lg",
          acceptButton: "!bg-white !text-black hover:!bg-gray-200",
          rejectButton: "!text-white hover:!underline",
          manageLink: "!text-white underline hover:!no-underline",
        }}
      />
      <ConsentDialog
        title="Cookie Preferences"
        intro="This website uses cookies, pixel tags, and local storage for performance and marketing purposes. We use our own cookies and some from third parties. If you opted to accept any non-essential cookies, you can change or customize your selection at any time below."
        privacyPolicyHref={"https://www.caroffshore.it/legal/privacy-policy"}
        privacyPolicyLabel="Privacy Policy"
        acceptAllLabel="Accept all"
        rejectAllLabel="Reject all"
        saveLabel="Save preferences"
        classNames={{
          overlay: "!bg-black/50",
          root: "!bg-black !text-white",
          header: "!border-[#252525]",
          title: "!text-white",
          closeButton: "hover:!bg-[#252525]",
          body: "!text-white",
          intro: "!text-white/90",
          footer: "!border-[#252525]",
          rejectAllButton: "!border-white hover:!bg-white hover:!text-black",
          acceptAllButton: "!border-white hover:!bg-white hover:!text-black",
          saveButton: "!bg-white !text-black hover:!bg-gray-200",
          categoryRow: "!border-[#252525]",
          categoryTitle: "!text-white",
          categoryDescription: "!text-white/80",
        }}
      />
    </>
  );
}

export default function ConsentRoot({
  children,
}: {
  children: React.ReactNode;
}) {
  const config: ConsentConfig = useMemo(
    () => ({
      categories: [
        {
          id: "necessary",
          name: "Essential",
          description: "Required for the website to work.",
          required: true,
        },
        {
          id: "analytics",
          name: "Analytics",
          description:
            "Help us understand visitor interactions, measure performance, and spot technical issues.",
        },
        {
          id: "marketing",
          name: "Marketing",
          description:
            "Help deliver relevant advertising and measure ad effectiveness.",
        },
        {
          id: "personalization",
          name: "Personalization",
          description:
            "Remember choices and personalize features (e.g. language, region).",
        },
      ],
      services: [
        { id: "essential", category: "necessary", name: "Essential" },
        {
          id: "posthog",
          category: "analytics",
          name: "PostHog",
          onActivate: () => posthog.opt_in_capturing(),
          onDeactivate: () => posthog.opt_out_capturing(),
        },
        {
          id: "harbor",
          category: "analytics",
          name: "Session recording",
          onActivate: () => {
            harbor.optIn();
            harbor.init({
              siteToken: HARBOR_SITE_TOKEN!,
              baseUrl: "https://api.harbor.studio3dx.com",
              requireConsent: true,
              autoTrack: true,
              sessionRecording: {
                enabled: true,
              },
            });
          },
          onDeactivate: () => harbor.optOut(),
        },
        {
          id: "gtag",
          category: "analytics",
          name: "Google Analytics",
          onActivate: () => {
            if (typeof window !== "undefined" && window.gtag) {
              window.gtag("consent", "update", {
                analytics_storage: "granted",
              });
              window.gtag("config", GA_ID!);
            }
          },
          onDeactivate: () => {
            if (typeof window !== "undefined" && window.gtag) {
              window.gtag("consent", "update", {
                analytics_storage: "denied",
              });
            }
          },
        },
      ],
    }),
    [],
  );

  return (
    <ConsentProvider config={config}>
      <GtagScript />
      {children}
      <ConsentUI />
    </ConsentProvider>
  );
}
